/**
 * <plw-story-player> — the shared story-video player surface
 * (snippets/plw-story-player.liquid; both the article.stories page and each
 * Stories-listing modal render it).
 *
 * Progressive enhancement over the native video controls: on connect it
 * strips the `controls` attribute and unhides the full-surface
 * `[data-story-toggle]` button — the brand play disc when paused, a matching
 * pause disc revealed on hover/focus while playing (CSS keys off the host's
 * `data-playing` attribute). With JS off the button stays hidden and the
 * native controls remain.
 *
 * Playback state is mirrored FROM the video's own play/pause events (not the
 * click handler), so external play/pause calls — <plw-story-modal> starts the
 * video on `dialog:open` and pauses it on close — keep the toggle in sync.
 *
 * While playing, the pause disc only shows while the pointer is ACTIVE over
 * the surface (`data-cursor-active`, dropped after 2s without movement) — in
 * the fullscreen modal the player IS the viewport, so a bare :hover rule
 * would pin the disc over the video for the whole watch.
 *
 * Mobile cut: when the <video> carries `data-mobile-src` (the article's 4:5
 * custom.video_mobile, emitted by the snippet) and the viewport is below the
 * 750px breakpoint at connect time, the markup's 16:9 sources are replaced
 * with that file (and its poster) before anything plays. Decided once, on
 * connect — swapping a source mid-watch on rotate would restart the video.
 */
class PlwStoryPlayer extends HTMLElement {
  static #CURSOR_IDLE_MS = 2000;
  static #MOBILE_QUERY = '(max-width: 749px)';

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #cursorTimer;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#toggle = this.querySelector('[data-story-toggle]');
    if (!this.#video || !this.#toggle) return;

    this.#video.removeAttribute('controls');
    this.#toggle.hidden = false;
    this.#applyMobileSource();

    this.#video.addEventListener('play', this.#reflectState);
    this.#video.addEventListener('pause', this.#reflectState);
    this.#toggle.addEventListener('click', this.#togglePlayback);
    this.addEventListener('pointermove', this.#wakeCursor, { passive: true });
    this.#reflectState();
  }

  disconnectedCallback() {
    this.#video?.removeEventListener('play', this.#reflectState);
    this.#video?.removeEventListener('pause', this.#reflectState);
    this.#toggle?.removeEventListener('click', this.#togglePlayback);
    this.removeEventListener('pointermove', this.#wakeCursor);
    clearTimeout(this.#cursorTimer);
  }

  /** Swap in the 4:5 mobile cut below the breakpoint (see the header comment). */
  #applyMobileSource() {
    const video = this.#video;
    if (!video) return;
    const { mobileSrc, mobilePoster } = video.dataset;
    if (!mobileSrc || !window.matchMedia(PlwStoryPlayer.#MOBILE_QUERY).matches) return;

    for (const source of video.querySelectorAll('source')) source.remove();
    const source = document.createElement('source');
    source.src = mobileSrc;
    source.type = 'video/mp4';
    video.append(source);
    if (mobilePoster) video.poster = mobilePoster;
    // Re-evaluate the source list; with preload="none" this downloads nothing.
    video.load();
  }

  #wakeCursor = () => {
    this.setAttribute('data-cursor-active', '');
    clearTimeout(this.#cursorTimer);
    this.#cursorTimer = setTimeout(() => {
      this.removeAttribute('data-cursor-active');
    }, PlwStoryPlayer.#CURSOR_IDLE_MS);
  };

  #togglePlayback = () => {
    if (!this.#video) return;
    if (this.#video.paused) {
      this.#video.play().catch(() => {});
    } else {
      this.#video.pause();
    }
  };

  #reflectState = () => {
    if (!this.#video || !this.#toggle) return;
    const playing = !this.#video.paused;
    this.toggleAttribute('data-playing', playing);
    const label = playing ? this.#toggle.dataset.labelPause : this.#toggle.dataset.labelPlay;
    if (label) this.#toggle.setAttribute('aria-label', label);
  };
}

if (!customElements.get('plw-story-player')) {
  customElements.define('plw-story-player', PlwStoryPlayer);
}
